/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useRef, useState } from 'react';

import config from 'js/app/config';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { get } from 'js/lib/user';

import { Button, Dialog, Link, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import InternationalPhoneNumberInput from 'bundles/account-profile/components/i18n-phone-number-input/InternationalPhoneNumberInput';
import type { ProfilePhone } from 'bundles/account-profile/components/types';
import { isDHubEoiOptEnabled, updateUserProfile } from 'bundles/expression-of-interest/utils/util';
import type { Phone } from 'bundles/naptimejs/resources/__generated__/ProfilesV1';
import { customFormattedPhoneStyles } from 'bundles/university-program-qualification/components/SurveyQuestion';

import _t from 'i18n!nls/expression-of-interest';

type Props = {
  phone: Phone;
  handleSubmit: () => Promise<void | {}>;
  navigateSelectSubject: () => void;
  navigateConfirmation: () => void;
  handlePhoneChange: (data?: ProfilePhone) => void;
  productId: string;
};

const styles = {
  phoneInput: css`
    padding-bottom: var(--cds-spacing-300);
  `,
  primaryButton: css`
    margin-right: var(--cds-spacing-200);
  `,
};

export const PhoneNumberSection = (props: Props): React.ReactElement => {
  const { handleSubmit, navigateConfirmation, navigateSelectSubject, productId, phone, handlePhoneChange } = props;

  const [fieldError, setFieldError] = useState<boolean>(false);
  const track = useTracker();
  const phoneNumberRef = useRef<HTMLInputElement>(null);

  const { HeadingGroup, Content, Actions } = Dialog;

  const handleNextSection = () => {
    if (isDHubEoiOptEnabled(productId)) {
      navigateSelectSubject();
    } else {
      navigateConfirmation();
    }
  };

  const handlePhoneSubmit = () => {
    if (!fieldError) {
      handleSubmit()
        .then(() => {
          const formattedPhone = phone;
          // user profile expects the phone number to be stripped of its country code
          formattedPhone.phoneNumber = phone.phoneNumber.replace(`+${phone.countryCode}`, '');
          const currentUser = get();
          updateUserProfile({
            userId: currentUser.id,
            externalId: currentUser.external_id,
            fullname: currentUser.fullName,
            phone: formattedPhone,
          });

          handleNextSection();

          track('submit_eoi', {
            eoiStatus: 'success',
            eoiSection: 'phone_number_section',
            pageSection: {
              sectionName: 'eoi',
              subindex: 1,
            },
          });
        })
        .catch(() => {
          track('submit_eoi', {
            eoiStatus: 'api_failure',
            eoiSection: 'phone_number_section',
            pageSection: {
              sectionName: 'eoi',
              subindex: 1,
            },
          });
        });
    } else {
      track('submit_eoi', {
        eoiStatus: 'invalid_submission',
        eoiSection: 'phone_number_section',
        pageSection: {
          sectionName: 'eoi',
          subindex: 1,
        },
      });
    }
  };

  const handlePhoneSkip = () => {
    handleNextSection();

    track('submit_eoi', {
      eoiStatus: 'skip',
      eoiSection: 'phone_number_section',
      pageSection: {
        sectionName: 'eoi',
        subindex: 1,
      },
    });
  };

  return (
    <>
      <HeadingGroup
        supportText={_t(
          'Adding a phone number is completely optional - by providing your phone number you agree that Coursera Enrollment Counselors may call or text you about educational opportunities.'
        )}
      >
        {_t('Would you like to add a phone number?')}
      </HeadingGroup>
      <Content>
        <div css={styles.phoneInput}>
          <InternationalPhoneNumberInput
            profile={{ phone }}
            customStyles={customFormattedPhoneStyles}
            handleFormChange={handlePhoneChange}
            handleError={(hasError: boolean) => {
              setFieldError(hasError);
            }}
            ref={phoneNumberRef}
          />
        </div>

        <Typography2 component="p" variant="bodySecondary" color="supportText">
          <FormattedMessage
            message={_t("You can opt-out any time. Further information is available in Coursera's {privacyNotice}.")}
            privacyNotice={<Link href={`${config.url.base}about/privacy`}>{_t('Privacy Notice')}</Link>}
          />
        </Typography2>
      </Content>
      <Actions>
        <Button onClick={handlePhoneSubmit} css={styles.primaryButton}>
          {_t('Add phone number')}
        </Button>
        <Button variant="secondary" onClick={handlePhoneSkip}>
          {_t('No thanks')}
        </Button>
      </Actions>
    </>
  );
};

export default PhoneNumberSection;
